import type { ReactNode } from "react";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label?: string;
  icon?: ReactNode;
  badge?: string | number;
  disabled?: boolean;
  activeColor?: string;
  activeBgColor?: string;
};

export type SegmentedControlProps<T extends string> = {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  variant?: "pill" | "outlined";
};

const sizeClasses = {
  sm: "px-2 py-1 text-xs",
  md: "px-3 py-1.5 text-sm",
  lg: "px-4 py-2 text-sm",
};

const containerPadding = {
  sm: "p-1",
  md: "p-0.5",
  lg: "p-1",
};

const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  size = "md",
  disabled = false,
  variant = "pill",
}: SegmentedControlProps<T>) => {
  if (variant === "outlined") {
    return (
      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
        {options.map((option, index) => {
          const isActive = value === option.value;
          const isDisabled = disabled || option.disabled;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={isDisabled}
              title={!option.label && option.icon ? option.value : undefined}
              className={`flex items-center gap-2 ${sizeClasses[size]} font-medium transition-colors ${
                index > 0 ? "border-l border-gray-300" : ""
              } ${
                isActive
                  ? "bg-green-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {option.icon}
              {option.label && <span>{option.label}</span>}
              {option.badge !== undefined && (
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium ${
                    isActive
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {option.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex rounded-lg bg-gray-100 ${containerPadding[size]}`}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        const isDisabled = disabled || option.disabled;

        const activeClasses = option.activeBgColor && option.activeColor
          ? `${option.activeBgColor} ${option.activeColor}`
          : "bg-white text-gray-900 shadow-sm";
        const inactiveClasses = "text-gray-500 hover:text-gray-700";

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={isDisabled}
            title={!option.label && option.icon ? option.value : undefined}
            className={`flex items-center gap-2 ${sizeClasses[size]} font-medium rounded-md transition-colors ${
              isActive ? activeClasses : inactiveClasses
            } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {option.icon}
            {option.label && <span>{option.label}</span>}
            {option.badge !== undefined && (
              <span
                className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium ${
                  isActive
                    ? option.activeBgColor && option.activeColor
                      ? `${option.activeBgColor} ${option.activeColor}`
                      : "bg-gray-100 text-gray-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
